import {
  Button,
  Form,
  Icon,
  Input,
  Modal,
  Tooltip,
  message,
  Card,
  Divider
} from 'antd'
import React, { useState, useRef, useEffect } from 'react'

import './ScenarioForm.scss'
import { CreatableSelect } from '../../../../../../../../../components'

import scenarioFormOptions from './scenarioFormOptions'
import { formatLabel } from '../../utils'
import { api } from '../../../../../../../../../services'
import { useTranslation } from 'react-i18next'
import { config } from '../../../../../../../../../constants'
import { getToken } from '../../../../../../../../../services/api/user'

function ScenarioForm({ onCancel, form, onSubmit, extractedText, language }) {
  const { getFieldDecorator, validateFields } = form

  const [isGeneratingScenario, setIsGeneratingScenario] = useState(false)
  const [generationMode, setGenerationMode] = useState('form') // 'form' or 'chat'
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [isChatLoading, setIsChatLoading] = useState(false)
  const [generatedScenario, setGeneratedScenario] = useState(null)
  const [isUpdatingScenario, setIsUpdatingScenario] = useState(false)
  const [hasStartedChat, setHasStartedChat] = useState(false)
  const [showScenarioModal, setShowScenarioModal] = useState(false)
  const [selectedScenario, setSelectedScenario] = useState(null)
  const chatContainerRef = useRef(null)
  const chatMessagesRef = useRef([])
  const typingTimers = useRef({})
  const { t } = useTranslation()

  // Keep chatMessagesRef in sync
  useEffect(() => {
    chatMessagesRef.current = chatMessages
  }, [chatMessages])

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [chatMessages])

  const parseScenarioData = scenarioText => {
    try {
      if (!scenarioText) return null

      let cleanedText = scenarioText.trim()

      // 1. Remove optional markdown code blocks
      cleanedText = cleanedText.replace(/^```json\s*|```$/g, '')

      // 2. Helper to clean the inside of a single-quoted string
      // This is called AFTER we capture the content, so we can safely:
      // - Escape internal double quotes (so they don't break our new "" delimiters)
      // - Unescape \' to ' (apostrophes are now safe inside double quotes)
      const cleanStringContent = content => {
        return content
          .replace(/"/g, '\\"') // Escape internal double quotes
          .replace(/\\'/g, "'") // Unescape apostrophes
      }

      // 3. Fix surgical single quotes around keys
      // Convert 'key': to "key":
      cleanedText = cleanedText.replace(/'([\w_]+)':/g, '"$1":')

      // 4. Convert : 'value' to : "value" for string values
      // The regex captures content between single quotes, allowing for escaped chars
      cleanedText = cleanedText.replace(
        /: '((?:[^'\\]|\\.)*)'/g,
        (match, p1) => `: "${cleanStringContent(p1)}"`
      )

      // 5. Convert ['value', 'value'] to ["value", "value"] for arrays
      cleanedText = cleanedText.replace(
        /\[\s*'((?:[^'\\]|\\.)*)'/g,
        (match, p1) => `["${cleanStringContent(p1)}"`
      )
      cleanedText = cleanedText.replace(
        /,\s*'((?:[^'\\]|\\.)*)'/g,
        (match, p1) => `, "${cleanStringContent(p1)}"`
      )

      // 4. Final safety: fix any remaining unescaped single quotes that are now delimiters
      // If the string starts with ' and ends with ' (after trim), convert to "
      if (
        cleanedText.charAt(0) === "'" &&
        cleanedText.charAt(cleanedText.length - 1) === "'"
      ) {
        cleanedText = '"' + cleanedText.slice(1, -1).replace(/"/g, '\\"') + '"'
      }

      // 5. Fix apostrophes that accidentally became escaped double quotes
      // (e.g. Jordan"s -> Jordan's)
      cleanedText = cleanedText
        .replace(/(\w+)"(t|s|re|ve|ll|d)\b/g, "$1'$2")
        .replace(/(\w+)"s(?=[\s,\]\}])/g, "$1's")
        .replace(/\b"([Ii])"([mv]|ll)\b/g, "'$1'$2")
        .replace(/(?<=[a-zA-Z])"(?=[a-zA-Z])/g, "'")

      const parsedData = JSON.parse(cleanedText)

      // Recursive function to find the scenario object in potentially nested arrays
      const findScenarioTarget = data => {
        if (!data) return null
        if (typeof data === 'object' && !Array.isArray(data)) {
          if (data.scenario || data.options) return data
          return null
        }
        if (Array.isArray(data)) {
          for (const item of data) {
            const found = findScenarioTarget(item)
            if (found) return found
          }
        }
        return null
      }

      const parsedScenario = findScenarioTarget(parsedData)
      if (parsedScenario) {
        return parsedScenario
      }
    } catch (error) {
      console.error('Scenario parsing failed:', error)
      console.log('Attempted cleanup text:', scenarioText)
    }

    return null
  }

  // Initialize chat when switching to chat mode
  const initializeChat = async () => {
    if (hasStartedChat) return

    setIsChatLoading(true)
    setHasStartedChat(true)
    const messageId = Date.now()

    // Add placeholder message that will be updated with streaming content
    setChatMessages([
      {
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        id: messageId,
        isStreaming: true,
        language
      }
    ])

    try {
      const data = {
        type: 'chat',
        course_content: extractedText,
        message: 'start'
      }

      const url = config.questionAnswerAiUrl
      const token = await getToken()

      const response = await fetch(`${url}/chat?service=scenario-generation`, {
        method: 'POST',
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      })

      if (!response.body) {
        throw new Error('ReadableStream not supported')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let accumulatedText = ''

      const readStream = async () => {
        try {
          const { done, value } = await reader.read()

          if (value) {
            const chunk = decoder.decode(value, { stream: true })
            accumulatedText += chunk
          }

          let chatContent = accumulatedText
          let scenarioContent = ''
          let parsedScenario = null
          let isEndFound = false

          if (accumulatedText.includes('<<END>>')) {
            isEndFound = true
            const parts = accumulatedText.split('<<END>>')
            chatContent = parts[0]
            scenarioContent = parts[1] || ''
            parsedScenario = parseScenarioData(scenarioContent)
            if (parsedScenario) {
              setGeneratedScenario(parsedScenario)
            }
          }

          setChatMessages(prev =>
            prev.map(msg =>
              msg.id === messageId
                ? {
                    ...msg,
                    targetContent: chatContent.trim(),
                    isStreaming: !done,
                    isStreamDone: done,
                    isEndFound,
                    hasScenario: !!parsedScenario,
                    scenarioData: parsedScenario,
                    language
                  }
                : msg
            )
          )

          // Start or update typing
          startTyping(messageId, chatContent.trim(), done)

          if (done) {
            setIsChatLoading(false)
            // Cleanup: ensure isStreaming is false and any remaining content is typed
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      isStreaming: false,
                      isTyping: false,
                      content: chatContent.trim()
                    }
                  : msg
              )
            )
            return
          }

          return readStream()
        } catch (error) {
          console.error('Stream read error:', error)
          setIsChatLoading(false)
          // Update message to show error state
          setChatMessages(prev =>
            prev.map(msg =>
              msg.id === messageId
                ? {
                    ...msg,
                    content: t('v5:sorry_encountered_an_error'),
                    isStreaming: false,
                    isStreamDone: true,
                    hasError: true
                  }
                : msg
            )
          )
          message.error(t('v5:sorry_encountered_an_error'))
        }
      }

      return readStream()
    } catch (error) {
      console.error('Error initializing chat:', error)
      message.error('Failed to initialize chat')
      setChatMessages([
        {
          type: 'assistant',
          content: t('v5:will_help_you_create_scenario'),
          timestamp: new Date(),
          id: messageId,
          isStreaming: false,
          language
        }
      ])
      setIsChatLoading(false)
    }
  }

  const handleChatSubmit = async e => {
    e && e.preventDefault()
    if (!chatInput.trim() || isChatLoading) return

    const userMessage = {
      type: 'user',
      content: chatInput.trim(),
      timestamp: new Date(),
      id: Date.now(),
      language
    }
    const messageId = Date.now() + 1

    // Add user message and placeholder for assistant response
    setChatMessages(prev => [
      ...prev,
      userMessage,
      {
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        id: messageId,
        isStreaming: true,
        language
      }
    ])

    setChatInput('')
    setIsChatLoading(true)

    try {
      const data = generatedScenario
        ? {
            type: 'update',
            generated_scenario: generatedScenario,
            message: userMessage.content,
            language
          }
        : {
            type: 'chat',
            course_content: extractedText,
            message: userMessage.content,
            language
          }

      const url = config.questionAnswerAiUrl
      const token = await getToken()

      const response = await fetch(`${url}/chat?service=scenario-generation`, {
        method: 'POST',
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      })

      if (!response.body) {
        throw new Error('ReadableStream not supported')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let accumulatedText = ''

      const readStream = async () => {
        try {
          const { done, value } = await reader.read()

          if (value) {
            const chunk = decoder.decode(value, { stream: true })
            accumulatedText += chunk
          }

          let chatContent = accumulatedText
          let scenarioContent = ''
          let parsedScenario = null
          let isEndFound = false

          if (accumulatedText.includes('<<END>>')) {
            isEndFound = true
            const parts = accumulatedText.split('<<END>>')
            chatContent = parts[0]
            scenarioContent = parts[1] || ''
            parsedScenario = parseScenarioData(scenarioContent)
            if (parsedScenario) {
              setGeneratedScenario(parsedScenario)
            }
          }

          setChatMessages(prev =>
            prev.map(msg =>
              msg.id === messageId
                ? {
                    ...msg,
                    targetContent: chatContent.trim(),
                    isStreaming: !done,
                    isStreamDone: done,
                    isEndFound,
                    hasScenario: !!parsedScenario,
                    scenarioData: parsedScenario,
                    language
                  }
                : msg
            )
          )

          // Start or update typing
          startTyping(messageId, chatContent.trim(), done)

          if (done) {
            setIsChatLoading(false)
            // Cleanup
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      isStreaming: false,
                      isTyping: false,
                      content: chatContent.trim()
                    }
                  : msg
              )
            )
            return
          }

          return readStream()
        } catch (error) {
          console.error('Stream read error:', error)
          setIsChatLoading(false)
          // Update message to show error state
          setChatMessages(prev =>
            prev.map(msg =>
              msg.id === messageId
                ? {
                    ...msg,
                    content: t('v5:sorry_encountered_an_error'),
                    isStreaming: false,
                    isStreamDone: true,
                    hasError: true
                  }
                : msg
            )
          )
          message.error(t('v5:sorry_encountered_an_error'))
        }
      }

      return readStream()
    } catch (error) {
      console.error('Error sending chat message:', error)
      message.error('Failed to send message')
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? {
                ...msg,
                content: t('v5:sorry_encountered_an_error'),
                isStreaming: false,
                language
              }
            : msg
        )
      )
      setIsChatLoading(false)
    }
  }

  const startTyping = (messageId, _unused, isDone) => {
    if (typingTimers.current[messageId]) {
      if (isDone) {
        setChatMessages(prev =>
          prev.map(m => (m.id === messageId ? { ...m, isStreamDone: true } : m))
        )
      }
      return
    }

    typingTimers.current[messageId] = setInterval(() => {
      setChatMessages(prev => {
        const msg = prev.find(m => m.id === messageId)
        if (!msg) {
          clearInterval(typingTimers.current[messageId])
          delete typingTimers.current[messageId]
          return prev
        }

        const currentContent = msg.content || ''
        const targetText = msg.targetContent || ''

        if (currentContent.length < targetText.length) {
          // Type characters
          const nextChars = targetText.slice(
            currentContent.length,
            currentContent.length + 3
          )
          return prev.map(m =>
            m.id === messageId
              ? { ...m, content: currentContent + nextChars }
              : m
          )
        } else if (msg.isStreamDone || isDone) {
          // Typing finished and stream is done
          clearInterval(typingTimers.current[messageId])
          delete typingTimers.current[messageId]
          return prev.map(m =>
            m.id === messageId ? { ...m, isTyping: false } : m
          )
        } else {
          // Waiting for more data from stream
          return prev
        }
      })
    }, 30)
  }

  const handleFormSubmit = e => {
    e.preventDefault()

    validateFields((err, values) => {
      if (!err) {
        setIsGeneratingScenario(true)
        console.log('Received values of form: ', values)
        const pageContent = extractedText

        generateScenario(pageContent, values)
      }
    })
  }

  const generateScenario = (pageContent, scenarioElements) => {
    const data = {
      type: 'normal',
      course_content: pageContent,
      scenario_elements: scenarioElements,
      language
    }

    api.courses
      .generateScenario(data)
      .then(res => {
        const resData = res.data.body && res.data.body[0]
        onSubmit(resData)
      })
      .catch(err => {
        console.log(err)
        message.error('Failed to generate scenario')
      })
      .finally(() => {
        setIsGeneratingScenario(false)
      })
  }

  const handleViewScenario = scenario => {
    setSelectedScenario(scenario)
    setShowScenarioModal(true)
  }

  const handleScenarioModalClose = () => {
    setShowScenarioModal(false)
    setSelectedScenario(null)
  }

  const handleUseScenario = () => {
    if (selectedScenario) {
      onSubmit(selectedScenario)
    } else if (generatedScenario) {
      onSubmit(generatedScenario)
    }
    handleScenarioModalClose()
  }

  const renderChat = () => (
    <div className="chat-container">
      {!hasStartedChat ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Card>
            <Icon
              type="message"
              style={{
                fontSize: '48px',
                color: '#1890ff',
                marginBottom: '16px'
              }}
            />
            <h3>{t('v5:chat_based_scenario_generation')}</h3>
            <p>{t('v5:start_conversation_to_generate')}</p>
            <Button
              type="primary"
              size="large"
              onClick={initializeChat}
              loading={isChatLoading}
            >
              {t('v5:start_chat')}
            </Button>
          </Card>
        </div>
      ) : (
        <>
          <div
            className="chat-messages"
            ref={chatContainerRef}
            style={{
              height: '350px',
              overflowY: 'auto',
              marginBottom: '16px',
              padding: '12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              backgroundColor: '#fafafa'
            }}
          >
            {chatMessages.map(msg => (
              <div
                key={msg.id}
                className={`chat-message-item ${msg.type}`}
                style={{ marginBottom: '16px' }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    backgroundColor: msg.type === 'user' ? '#1890ff' : '#fff',
                    color: msg.type === 'user' ? 'white' : 'black',
                    marginLeft: msg.type === 'user' ? '20%' : '0',
                    marginRight: msg.type === 'user' ? '0' : '20%',
                    position: 'relative',
                    border:
                      msg.type === 'assistant' ? '1px solid #e8e8e8' : 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                    {msg.content || (
                      <span style={{ fontStyle: 'italic', opacity: 0.6 }}>
                        {t('v5:processing_scenario_details')}
                      </span>
                    )}
                    {!msg.hasScenario &&
                      msg.content &&
                      msg.content === msg.targetContent &&
                      (msg.isStreaming || !msg.isStreamDone) && (
                        <div
                          style={{
                            marginTop: '8px',
                            fontStyle: 'italic',
                            opacity: 0.6,
                            fontSize: '12px'
                          }}
                        >
                          {msg.isEndFound
                            ? t('v5:finalizing_test_components')
                            : t('v5:generating_scenario_wait')}
                        </div>
                      )}
                  </div>
                  {msg.isStreaming &&
                    (msg.content !== msg.targetContent || !msg.isEndFound) && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: '2px',
                          height: '16px',
                          backgroundColor:
                            msg.type === 'user'
                              ? 'rgba(255,255,255,0.8)'
                              : '#1890ff',
                          marginLeft: '4px',
                          animation: 'blink 1s infinite'
                        }}
                      ></span>
                    )}
                  {msg.hasScenario && (
                    <div style={{ marginTop: '12px' }}>
                      <Button
                        type="primary"
                        size="small"
                        icon="eye"
                        onClick={() =>
                          handleViewScenario(
                            msg.scenarioData || generatedScenario
                          )
                        }
                      >
                        {t('v5:view_scenario')}
                      </Button>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#999',
                    marginTop: '4px',
                    textAlign: msg.type === 'user' ? 'right' : 'left'
                  }}
                >
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>

          <Form
            onSubmit={handleChatSubmit}
            style={{ display: 'flex', gap: '8px' }}
          >
            <Input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              placeholder="Type your message..."
              onPressEnter={handleChatSubmit}
              disabled={isChatLoading}
              style={{ borderRadius: '20px' }}
            />
            <Button
              type="primary"
              htmlType="submit"
              loading={isChatLoading}
              disabled={!chatInput.trim()}
              shape="round"
            >
              {t('v3:send')}
            </Button>
          </Form>
        </>
      )}
    </div>
  )

  const renderForm = () => (
    <Form className="scenario-form" onSubmit={handleFormSubmit}>
      {Object.entries(scenarioFormOptions).map(([key, values]) => (
        <Form.Item
          labelAlign="top"
          label={
            <span className="scenario-form-label">
              <span>{formatLabel(key)} </span>
              <Tooltip title={t(values.description)}>
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          key={key}
        >
          {getFieldDecorator(key, {
            initialValue: Array.isArray(values.value) ? [] : values.value,
            rules: [
              {
                required: true,
                message: `${t('v4:please_input_your')} ${formatLabel(key)}!`
              }
            ]
          })(
            values.value &&
              Array.isArray(values.value) &&
              values.value.length > 0 ? (
              <CreatableSelect
                fieldKey={key}
                updateOptionValue={(value, field) =>
                  form.setFieldsValue({ [field]: value })
                }
                options={values.value}
                placeholder={`${t('v4:select_or_type_your')} ${formatLabel(
                  key
                )}`}
              />
            ) : (
              <Input.TextArea
                placeholder={`${t('v4:enter_your')} ${formatLabel(key)}`}
              />
            )
          )}
        </Form.Item>
      ))}
      <div className="form-button-center">
        <Form.Item>
          <Button
            className="shl-primary-btn"
            htmlType="submit"
            shape="round"
            size="large"
            loading={isGeneratingScenario}
          >
            {t('v4:generate_world_summary')}
          </Button>
        </Form.Item>
      </div>
    </Form>
  )

  const renderScenarioModal = () => {
    if (!selectedScenario) return null

    const correctAnswerIndex = parseInt(selectedScenario.correct_answer) - 1

    return (
      <Modal
        title="Generated Scenario Details"
        visible={showScenarioModal}
        onCancel={handleScenarioModalClose}
        footer={[
          <Button key="cancel" onClick={handleScenarioModalClose}>
            {t('v3:close')}
          </Button>,
          <Button key="use" type="primary" onClick={handleUseScenario}>
            {t('v5:use_this_scenario')}
          </Button>
        ]}
        width={800}
      >
        <div className="scenario-details">
          <div className="scenario-section" style={{ marginBottom: '24px' }}>
            <h4 style={{ color: '#1890ff', marginBottom: '12px' }}>
              {t('v4:scenario_escription')}
            </h4>
            <div
              style={{
                backgroundColor: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}
            >
              {selectedScenario.scenario}
            </div>
          </div>

          <div className="scenario-section" style={{ marginBottom: '24px' }}>
            <h4 style={{ color: '#1890ff', marginBottom: '12px' }}>Options:</h4>
            <div>
              {selectedScenario.options &&
                selectedScenario.options.map((option, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor:
                        index === correctAnswerIndex ? '#f6ffed' : '#fafafa',
                      border: `1px solid ${
                        index === correctAnswerIndex ? '#b7eb8f' : '#e8e8e8'
                      }`,
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        minWidth: '20px'
                      }}
                    >
                      {String.fromCharCode(65 + index)})
                    </span>
                    <span style={{ flex: 1 }}>{option}</span>
                    {index === correctAnswerIndex && (
                      <Icon type="check-circle" style={{ color: '#52c41a' }} />
                    )}
                  </div>
                ))}
            </div>
          </div>

          <div className="scenario-section">
            <h4 style={{ color: '#1890ff', marginBottom: '12px' }}>
              {t('v4:feedback')}:
            </h4>
            <div
              style={{
                backgroundColor: '#e6f7ff',
                padding: '16px',
                borderRadius: '8px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}
            >
              {selectedScenario.feedback}
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  // Reset chat when switching modes
  useEffect(() => {
    if (generationMode === 'chat') {
      if (!hasStartedChat) {
        setChatMessages([])
        setGeneratedScenario(null)
      }
    } else {
      // Keep the generated scenario when switching to form mode
    }
  }, [generationMode])

  return (
    <>
      <Modal
        title={t('v5:scenario_generation')}
        visible
        footer={null}
        centered
        closable
        onCancel={onCancel}
        className="scenario-generation"
        width={900}
      >
        <style>
          {`
            @keyframes blink {
              0%, 50% { opacity: 1; }
              51%, 100% { opacity: 0; }
            }
            .chat-message.user {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
            }
            .chat-message.assistant {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }
          `}
        </style>

        <div style={{ marginBottom: '16px' }}>
          <Button.Group>
            <Button
              type={generationMode === 'form' ? 'primary' : 'default'}
              onClick={() => setGenerationMode('form')}
            >
              <Icon type="form" /> {t('v4:form_mode')}
            </Button>
            <Button
              type={generationMode === 'chat' ? 'primary' : 'default'}
              onClick={() => setGenerationMode('chat')}
            >
              <Icon type="message" /> {t('v4:chat_mode')}
            </Button>
          </Button.Group>
        </div>

        <Divider />

        {generationMode === 'form' ? renderForm() : renderChat()}
      </Modal>

      {/* Scenario Details Modal */}
      {renderScenarioModal()}
    </>
  )
}

export default Form.create()(ScenarioForm)
